// Barber Matching AI System Types

// Enums
export enum ChattyLevel {
  Silent = 1,
  Minimal = 2,
  Moderate = 3,
  Conversational = 4,
  VeryTalkative = 5
}

export enum ServiceSpeed {
  Quick = 'Quick',
  Balanced = 'Balanced',
  Thorough = 'Thorough'
}

export enum Language {
  English = 'English',
  Spanish = 'Spanish',
  French = 'French',
  Mandarin = 'Mandarin',
  Other = 'Other'
}

export enum MatchingFactor {
  StyleMatch = 'StyleMatch',
  PersonalityMatch = 'PersonalityMatch',
  ExperienceMatch = 'ExperienceMatch',
  AvailabilityMatch = 'AvailabilityMatch',
  PriceMatch = 'PriceMatch',
  LanguageMatch = 'LanguageMatch',
  CustomerFeedback = 'CustomerFeedback'
}

// Customer Profile
export interface CustomerMatchingProfile {
  id: number;
  userId: string;
  userName: string;
  userEmail: string;
  preferredChattyLevel: ChattyLevel;
  preferredStyles: string[];
  preferredServiceSpeed: ServiceSpeed;
  preferredLanguages: Language[];
  budgetMin?: number;
  budgetMax?: number;
  flexibleOnPrice: boolean;
  importanceWeights: MatchImportanceWeights;
  createdAt: string;
  updatedAt: string;
}

export interface MatchImportanceWeights {
  styleWeight: number;
  personalityWeight: number;
  experienceWeight: number;
  availabilityWeight: number;
  priceWeight: number;
}

export interface UpdateCustomerMatchingProfileDto {
  preferredChattyLevel?: ChattyLevel;
  preferredStyles?: string[];
  preferredServiceSpeed?: ServiceSpeed;
  preferredLanguages?: Language[];
  budgetMin?: number;
  budgetMax?: number;
  flexibleOnPrice?: boolean;
  importanceWeights?: MatchImportanceWeights;
}

// Barber Profile
export interface BarberMatchingProfile {
  id: number;
  barberId: number;
  barberName: string;
  barbershopId: number;
  barbershopName: string;
  chattyLevel: ChattyLevel;
  specialtyStyles: string[];
  serviceSpeed: ServiceSpeed;
  languagesSpoken: Language[];
  typicalPriceRange: PriceRange;
  experienceYears: number;
  averageRating?: number;
  totalBookings: number;
  successfulMatches: number;
  matchSuccessRate: number;
  availability: BarberAvailability;
  createdAt: string;
  updatedAt: string;
}

export interface PriceRange {
  min: number;
  max: number;
  average: number;
}

export interface BarberAvailability {
  isAvailable: boolean;
  nextAvailableSlot?: string;
  availableThisWeek: boolean;
  typicalWaitDays: number;
}

export interface UpdateBarberMatchingProfileDto {
  barberId: number;
  chattyLevel?: ChattyLevel;
  specialtyStyles?: string[];
  serviceSpeed?: ServiceSpeed;
  languagesSpoken?: Language[];
  experienceYears?: number;
}

// Matching & Recommendations
export interface BarberMatchingScore {
  id: number;
  customerId: string;
  customerName: string;
  barberId: number;
  barberName: string;
  barbershopId: number;
  matchScore: number;
  matchPercentage: number;
  styleMatchScore: number;
  personalityMatchScore: number;
  experienceMatchScore: number;
  availabilityScore: number;
  priceMatchScore: number;
  languageMatchScore: number;
  customerFeedbackScore: number;
  matchReasons: string[];
  potentialConcerns: string[];
  recommendationStrength: RecommendationStrength;
  calculatedAt: string;
}

export enum RecommendationStrength {
  Weak = 'Weak',
  Moderate = 'Moderate',
  Strong = 'Strong',
  Excellent = 'Excellent'
}

export interface MatchRecommendation {
  barber: BarberMatchingProfile;
  matchingScore: BarberMatchingScore;
  isBestMatch: boolean;
  rank: number;
}

export interface DetailedMatchRecommendation extends MatchRecommendation {
  factorBreakdown: MatchFactorBreakdown[];
  whyRecommended: string[];
  potentialDrawbacks: string[];
  customerSuccessRate: number;
  estimatedSatisfaction: number;
}

export interface MatchFactorBreakdown {
  factor: MatchingFactor;
  factorName: string;
  score: number;
  weight: number;
  weightedScore: number;
  description: string;
}

export interface BestMatchResult {
  recommendation: DetailedMatchRecommendation;
  alternativeMatches: MatchRecommendation[];
  matchingSummary: string;
}

// Search Request
export interface SearchBarbersWithMatchingDto {
  barbershopId: number;
  preferredStyles?: string[];
  budgetMin?: number;
  budgetMax?: number;
  preferredChattyLevel?: ChattyLevel;
  preferredServiceSpeed?: ServiceSpeed;
  preferredLanguages?: Language[];
  mustBeAvailableToday?: boolean;
}

export interface SearchBarbersResult {
  matches: MatchRecommendation[];
  searchCriteria: SearchBarbersWithMatchingDto;
  totalResults: number;
  hasCustomerProfile: boolean;
}

// Feedback Tracking
export interface DisplayedRecommendationDto {
  customerId: string;
  barbershopId: number;
  displayedBarberIds: number[];
  topRecommendationBarberId: number;
  displayedAt: string;
  context?: string;
}

export interface BookingFeedbackDto {
  customerId: string;
  barberId: number;
  bookingId: number;
  wasRecommended: boolean;
  recommendationRank?: number;
  matchScore?: number;
  customerSatisfaction?: number;
}

export interface BookingMatchFeedback {
  id: number;
  bookingId: number;
  customerId: string;
  barberId: number;
  wasRecommended: boolean;
  recommendationRank?: number;
  matchScore?: number;
  customerSatisfaction?: number;
  feedbackNotes?: string;
  recordedAt: string;
}

// Analytics
export interface CustomerMatchingAnalytics {
  customerId: string;
  totalRecommendationsViewed: number;
  totalBookingsFromRecommendations: number;
  conversionRate: number;
  averageMatchScoreBooked: number;
  topMatchedBarbers: TopMatchedBarber[];
  preferenceAccuracy: number;
  lastUpdated: string;
}

export interface TopMatchedBarber {
  barberId: number;
  barberName: string;
  timesRecommended: number;
  timesBooked: number;
  averageMatchScore: number;
  customerSatisfaction?: number;
}

export interface BarberMatchingAnalytics {
  barberId: number;
  barberName: string;
  totalRecommendations: number;
  totalBookingsFromRecommendations: number;
  conversionRate: number;
  averageMatchScore: number;
  matchSuccessRate: number;
  topMatchingCustomerSegments: MatchingCustomerSegment[];
  lastUpdated: string;
}

export interface MatchingCustomerSegment {
  segmentName: string;
  matchCount: number;
  averageMatchScore: number;
  conversionRate: number;
}

export interface SystemPerformanceMetrics {
  totalMatchesCalculated: number;
  totalRecommendationsDisplayed: number;
  totalBookingsFromRecommendations: number;
  overallConversionRate: number;
  averageMatchAccuracy: number;
  topPerformingBarbers: BarberPerformanceSummary[];
  matchingTrends: MatchingTrend[];
  lastUpdated: string;
}

export interface BarberPerformanceSummary {
  barberId: number;
  barberName: string;
  barbershopName: string;
  recommendationCount: number;
  bookingCount: number;
  conversionRate: number;
  averageMatchScore: number;
  customerSatisfaction: number;
}

export interface MatchingTrend {
  date: string;
  recommendationsDisplayed: number;
  bookingsFromRecommendations: number;
  conversionRate: number;
  averageMatchScore: number;
}

// UI Helper Types
export interface PreferenceFormData {
  chattyLevel: ChattyLevel;
  styles: string[];
  serviceSpeed: ServiceSpeed;
  languages: Language[];
  budgetMin?: number;
  budgetMax?: number;
  flexibleOnPrice: boolean;
  weights: {
    style: number;
    personality: number;
    experience: number;
    availability: number;
    price: number;
  };
}

export interface MatchingInsightsProps {
  matchingScore: BarberMatchingScore;
  barberProfile: BarberMatchingProfile;
  customerProfile: CustomerMatchingProfile;
  showDetailed?: boolean;
}

// Common style options
export const COMMON_STYLES = [
  'Fade',
  'Taper',
  'Undercut',
  'Pompadour',
  'Buzz Cut',
  'Crew Cut',
  'Quiff',
  'Slick Back',
  'Side Part',
  'Mohawk',
  'Faux Hawk',
  'Textured Crop',
  'French Crop',
  'Bowl Cut',
  'Afro',
  'Dreadlocks',
  'Braids',
  'High Top',
  'Low Fade',
  'High Fade',
  'Mid Fade',
  'Skin Fade',
  'Drop Fade',
  'Burst Fade',
  'Temple Fade',
  'Beard Trim',
  'Lineup',
  'Hot Towel Shave',
  'Kids Cut',
  'Senior Cut'
];

// Language options
export const SUPPORTED_LANGUAGES = [
  { value: Language.English, label: 'English' },
  { value: Language.Spanish, label: 'Spanish' },
  { value: Language.French, label: 'French' },
  { value: Language.Mandarin, label: 'Mandarin' },
  { value: Language.Other, label: 'Other' }
];

// Chatty level descriptions
export const CHATTY_LEVEL_DESCRIPTIONS = {
  [ChattyLevel.Silent]: 'Minimal conversation - focus on the cut',
  [ChattyLevel.Minimal]: 'Brief exchanges only',
  [ChattyLevel.Moderate]: 'Some conversation, comfortable silence',
  [ChattyLevel.Conversational]: 'Good conversation throughout',
  [ChattyLevel.VeryTalkative]: 'Lots of conversation and interaction'
};

// Service speed descriptions
export const SERVICE_SPEED_DESCRIPTIONS = {
  [ServiceSpeed.Quick]: 'Fast service, in and out quickly',
  [ServiceSpeed.Balanced]: 'Balanced pace, quality and efficiency',
  [ServiceSpeed.Thorough]: 'Takes time, attention to detail'
};

// ===========================
// PHASE 2: Reviews & Social Proof
// ===========================

/**
 * Barber Review Summary (Phase 2)
 * Enhanced reviews with sentiment analysis and highlights
 */
export interface BarberReviewSummary {
  reviewId: number;
  customerInitials?: string;
  rating: number; // 1-5
  reviewText: string;
  reviewSnippet: string;
  createdAt: string;
  timeAgo: string;
  isVerified: boolean;
  photos?: string[];
  highlights: string[];
}

/**
 * Social Proof Data (Phase 2)
 * Comprehensive social proof metrics for barbers
 */
export interface SocialProof {
  similarCustomersCount: number;
  similarCustomersWhoBooked: number;
  similarCustomerBookingRate: number;
  totalBookingsLast30Days: number;
  totalBookingsLast7Days: number;
  isTrending: boolean;
  trendingPercentage: number;
  mostPopularDayOfWeek: string;
  mostPopularTimeSlot: string;
  returnCustomerRate: number;
  averageBookingsPerCustomer: number;
  rankInBarbershop: number;
  totalBarbersInShop: number;
  isTopPerformer: boolean;
  popularWith: string[];
  frequentlyBookedFor: string[];
}

/**
 * Barber Availability Status (Phase 2)
 * Real-time availability with slot information
 */
export interface BarberAvailabilityStatus {
  barberId: number;
  isCurrentlyAvailable: boolean;
  isOnBreak: boolean;
  breakEndsAt?: string;
  nextAvailableSlot?: string;
  estimatedWaitMinutes: number;
  currentQueuePosition: number;
  totalInQueue: number;
  availableSlotsToday: string[];
  availableSlotsTomorrow: string[];
  lastUpdated: string;
}

/**
 * AI Barber Recommendation (Phase 2 Enhanced)
 * Includes reviews, social proof, and sentiment analysis
 */
export interface AIBarberRecommendation {
  barber: BarberMatchingProfile;
  matchingScore: BarberMatchingScore;
  isBestMatch: boolean;
  rank: number;
  // Phase 2 additions
  recentReviews: BarberReviewSummary[];
  socialProof?: SocialProof;
  reviewSentimentScore: number;
  topReviewHighlights: string[];
}

/**
 * Enhanced Match Recommendation (extends existing)
 */
export interface EnhancedMatchRecommendation extends DetailedMatchRecommendation {
  recentReviews: BarberReviewSummary[];
  socialProof?: SocialProof;
  reviewSentimentScore: number;
  topReviewHighlights: string[];
  availabilityStatus?: BarberAvailabilityStatus;
}
